import { DateTime } from 'luxon';

import { EmptyTextData } from 'library/types';

type InputDate = Date | null | undefined;
type LuxonDateRange = {
	start: DateTime | null;
	end: DateTime | null;
};

const DATE_FORMAT = 'yyyy-MM-dd';
const DATE_RANGE_DELIMITER = ' - ';

const formatDate = (
	date: InputDate,
	formattingFunction: (
		date: Date,
		locales?: Intl.LocalesArgument,
		options?: Intl.DateTimeFormatOptions
	) => string,
	options?: Intl.DateTimeFormatOptions
) =>
	date instanceof Date
		? formattingFunction(date, window.navigator.language, options)
		: '';

export const getLocalizedTimeFormat = (
	inputDate: InputDate,
	options?: Intl.DateTimeFormatOptions
) =>
	formatDate(
		inputDate,
		(date, locale, options) => date.toLocaleTimeString(locale, options),
		options
	);

export const getLocalizedDateFormat = (
	inputDate: InputDate,
	options?: Intl.DateTimeFormatOptions
) =>
	formatDate(
		inputDate,
		(date, locale, options) => date.toLocaleDateString(locale, options),
		options
	);

export const getLocalizedDateTimeFormat = (
	inputDate: InputDate,
	options?: Intl.DateTimeFormatOptions
) =>
	formatDate(
		inputDate,
		(date, locale, options) => date.toLocaleString(locale, options),
		options
	);

export const fromIsoToLuxonDate = (iso: string) =>
	DateTime.fromISO(iso, {
		setZone: true,
		zone: 'utc',
	});

export const fromJsDateToLuxonDate = (
	date: Date | undefined | null
): DateTime | null =>
	date != null ? DateTime.fromJSDate(date, { zone: 'utc' }) : null;

export const fromIsoToDate = (iso: string) =>
	fromIsoToLuxonDate(iso).toJSDate();

export const isValidDate = (input: EmptyTextData): boolean => {
	if (!input) {
		return false;
	}

	const date = new Date(input);
	return !isNaN(date.getDate());
};

export const fromLuxonDateToStringDate = (
	date?: DateTime,
	isDefault = true,
	format = DATE_FORMAT
) =>
	date?.toFormat(format) ??
	(isDefault ? DateTime.now().toFormat(format) : '');

const fromStringDateToLuxonDate = (
	value?: string,
	format = DATE_FORMAT
): DateTime | null => {
	if (!value) {
		return null;
	}

	const date = DateTime.fromFormat(value, format, {
		zone: 'utc',
	});

	return date.isValid ? date : null;
};

export const fromLuxonDateRangeToString = (start: DateTime, end: DateTime) =>
	`${fromLuxonDateToStringDate(start)}${DATE_RANGE_DELIMITER}${fromLuxonDateToStringDate(end)}`;

export const isValidLuxonDateRangeString = (
	value?: string,
	format = DATE_FORMAT
): boolean => {
	const dateRange = fromStringDateRangeToLuxonDateRange(value, format);

	return dateRange.start !== null && dateRange.end !== null;
};

export const fromStringDateRangeToLuxonDateRange = (
	value?: string,
	format = DATE_FORMAT
): LuxonDateRange => {
	if (!value) {
		return {
			start: null,
			end: null,
		};
	}

	const [start, end, ...extraParts] = value.split(DATE_RANGE_DELIMITER);

	if (!start || !end || extraParts.length > 0) {
		return {
			start: null,
			end: null,
		};
	}

	return {
		start: fromStringDateToLuxonDate(start, format),
		end: fromStringDateToLuxonDate(end, format),
	};
};

export const fromStringDateRangeToStringRange = (
	value?: string
): { start: string | null; end: string | null } => {
	if (!value) {
		return {
			start: null,
			end: null,
		};
	}

	const [start, end, ...extraParts] = value.split(DATE_RANGE_DELIMITER);

	if (!start || !end || extraParts.length > 0) {
		return {
			start: null,
			end: null,
		};
	}

	return {
		start,
		end,
	};
};
